import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
  Grid,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { MenuBook } from "@mui/icons-material";
import { useState } from "react";

const FlashcardSection = ({
  loading,
  flashcardSets,
  activeDeckIndex,
  onSelectDeck,
  activeDeckCards,
  orderedCards,
  currentCard,
  studyIndex,
  studyFlipped,
  onToggleFlip,
  onPrevCard,
  onNextCard,
  onShuffle,
}) => {
  const theme = useTheme();
  const [selectedDeckIndex, setSelectedDeckIndex] = useState(null);
  const isControlled = activeDeckIndex !== undefined;

  const clearSelection = () => {
    if (isControlled) {
      onSelectDeck?.(null);
    }
    setSelectedDeckIndex(null);
  };

  const handleSelectDeck = (index) => {
    if (!isControlled) {
      setSelectedDeckIndex(index);
    }
    onSelectDeck?.(index);
  };

  // Use prop if provided, otherwise use internal state
  const effectiveDeckIndex = isControlled ? activeDeckIndex : selectedDeckIndex;

  if (effectiveDeckIndex === null) {
    // Show deck list only
    return (
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, height: "100%" }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <MenuBook color="primary" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5">Flashcards</Typography>
              <Typography variant="body2" color="text.secondary">
                Quick memory boosts with auto-shuffled flip cards.
              </Typography>
            </Box>
            <Chip
              label={loading ? "Loading..." : `${flashcardSets.length} decks`}
              color="secondary"
              variant="outlined"
            />
          </Stack>

          {flashcardSets.length === 0 && !loading && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1">No flashcards yet</Typography>
              <Typography variant="body2" color="text.secondary">
                Add flashcard decks for this grade to see them here.
              </Typography>
            </Paper>
          )}

          {flashcardSets.length > 0 && (
            <Stack spacing={1.5}>
              {flashcardSets.map((deck, index) => (
                <Button
                  key={`${deck.title}-${index}`}
                  variant="outlined"
                  color="primary"
                  onClick={() => handleSelectDeck(index)}
                  sx={{ justifyContent: "space-between", textAlign: "left" }}
                >
                  <Stack alignItems="flex-start">
                    <Typography variant="subtitle2">{deck.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {deck.topic || "Practice and learn"}
                    </Typography>
                  </Stack>
                  <Chip size="small" label={`${deck.cards.length} cards`} />
                </Button>
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>
    );
  }

  // Show selected deck study mode
  const deck = flashcardSets[effectiveDeckIndex];

  if (!deck) {
    return (
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, height: "100%" }}>
        <Typography>Deck not found</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, height: "100%" }}>
      <Button
        variant="outlined"
        onClick={clearSelection}
        sx={{ mb: 2 }}
      >
        ← Back to deck list
      </Button>

      <Stack spacing={2.5}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <MenuBook color="primary" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">{deck.title}</Typography>
            {deck.topic && (
              <Typography variant="body2" color="text.secondary">
                {deck.topic}
              </Typography>
            )}
          </Box>
        </Stack>

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
          }}
        >
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label={`${activeDeckCards.length} cards`} />
              <Chip
                label={
                  activeDeckCards.length
                    ? `Card ${studyIndex + 1} / ${activeDeckCards.length}`
                    : "0 / 0"
                }
                color="primary"
              />
            </Stack>

            {activeDeckCards.length === 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">No cards yet</Typography>
                <Typography variant="body2" color="text.secondary">
                  Add flashcards to start studying this deck.
                </Typography>
              </Paper>
            )}

            {activeDeckCards.length > 0 && currentCard && (
              <Paper
                role="button"
                tabIndex={0}
                onClick={onToggleFlip}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onToggleFlip();
                  }
                }}
                variant="outlined"
                sx={{
                  p: 3,
                  minHeight: 200,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  bgcolor: theme.palette.background.paper,
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "scale(1.01)",
                  },
                }}
              >
                <Typography variant="overline" color="text.secondary">
                  {studyFlipped ? "Answer" : "Question"}
                </Typography>
                <Typography variant="h6" sx={{ my: 2 }}>
                  {studyFlipped ? currentCard.back : currentCard.front}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  Tap to flip
                </Typography>
              </Paper>
            )}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                variant="outlined"
                onClick={onPrevCard}
                disabled={studyIndex === 0}
              >
                ← Previous
              </Button>
              <Button
                variant="outlined"
                onClick={onToggleFlip}
                disabled={activeDeckCards.length === 0}
              >
                Flip Card
              </Button>
              <Button
                variant="outlined"
                onClick={onShuffle}
                disabled={activeDeckCards.length === 0}
              >
                Shuffle
              </Button>
              <Button
                variant="contained"
                onClick={onNextCard}
                disabled={studyIndex >= orderedCards.length - 1}
              >
                Next →
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </Paper>
  );
};

export default FlashcardSection;
